// Loading skeletons shared by a page and the route fallback in App.jsx that
// shows while the page's code downloads. Both render the same markup, so the
// hand-off from "code loading" to "data loading" is invisible.

// Request detail: header, fields panel on the left, document viewer on the right.
export function RequestDetailSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading" className="h-screen flex flex-col bg-gray-50 dark:bg-gray-950 overflow-hidden">
      <div className="px-6 py-3.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="skeleton h-9 w-9 rounded-lg" />
          <div className="flex flex-col gap-2">
            <div className="skeleton h-4 w-40 rounded" />
            <div className="skeleton h-2.5 w-56 rounded" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="skeleton h-7 w-20 rounded-full" />
          <div className="skeleton h-7 w-24 rounded-lg" />
        </div>
      </div>
      <div className="flex-1 flex overflow-hidden min-w-0">
        <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
          <div className="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-4 mb-3">
              {[16, 20, 18, 24].map((w, i) => (
                <div key={i} className="skeleton h-3.5 rounded" style={{ width: `${w}px` }} />
              ))}
            </div>
            <div className="skeleton h-5 w-44 rounded" />
          </div>
          <div className="flex-1 overflow-hidden px-4 py-3 space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-xl border border-gray-200 dark:border-gray-700/70 p-3 space-y-2.5"
              >
                <div className="skeleton h-3 w-28 rounded" />
                <div className="skeleton h-8 w-full rounded-lg" />
                <div className="skeleton h-8 w-full rounded-lg" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 bg-gray-300 dark:bg-[#2a2d30] flex items-center justify-center p-6">
          <div className="w-full max-w-md aspect-[8.5/11] bg-white dark:bg-gray-900 rounded-md shadow-sm p-5 flex flex-col gap-2.5">
            <div className="skeleton h-4 w-2/3 rounded" />
            {[95, 88, 100, 72, 90].map((w, i) => (
              <div key={i} className="skeleton h-2 rounded" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const ROW_WIDTHS = [38, 52, 44, 30, 48, 36, 42, 50]

// Rows of a list or table: icon, two lines of text, a status pill. Use it in
// the card or table body that the rows will fill.
export function ListRowsSkeleton({ rows = 6 }) {
  return (
    <div aria-busy="true" aria-label="Loading" className="divide-y divide-gray-100 dark:divide-gray-800">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4">
          <div className="skeleton h-8 w-8 rounded-lg" />
          <div className="flex-1 flex flex-col gap-2">
            <div className="skeleton h-3.5 rounded" style={{ width: `${ROW_WIDTHS[i % ROW_WIDTHS.length]}%` }} />
            <div className="skeleton h-2.5 w-1/4 rounded" />
          </div>
          <div className="skeleton h-6 w-20 rounded-full" />
        </div>
      ))}
    </div>
  )
}

// Settings-style tab: cards of labelled inputs.
export function FormSkeleton({ sections = 2, fields = 3 }) {
  return (
    <div aria-busy="true" aria-label="Loading" className="space-y-4">
      {Array.from({ length: sections }, (_, s) => (
        <div key={s} className="rounded-xl border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-900 p-5 space-y-4">
          <div className="flex flex-col gap-2">
            <div className="skeleton h-4 w-40 rounded" />
            <div className="skeleton h-2.5 w-64 max-w-full rounded" />
          </div>
          {Array.from({ length: fields }, (_, f) => (
            <div key={f} className="space-y-2">
              <div className="skeleton h-3 w-28 rounded" />
              <div className="skeleton h-9 w-full rounded-lg" />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// Page heading: icon tile, title, one line of description.
function HeadingSkeleton() {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="skeleton h-10 w-10 rounded-xl" />
      <div className="flex flex-col gap-2">
        <div className="skeleton h-5 w-48 rounded" />
        <div className="skeleton h-3 w-72 max-w-[60vw] rounded" />
      </div>
    </div>
  )
}

// Any other page: title, toolbar, then a list of rows.
export function PageSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading">
      <HeadingSkeleton />
      <div className="flex items-center gap-3 mb-4">
        <div className="skeleton h-9 w-64 rounded-lg" />
        <div className="skeleton h-9 w-28 rounded-lg" />
        <div className="ml-auto skeleton h-9 w-32 rounded-lg" />
      </div>
      <div className="rounded-xl border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-900">
        <ListRowsSkeleton />
      </div>
    </div>
  )
}

// Dashboard: a row of KPI tiles, then two chart cards.
export function DashboardSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading">
      <HeadingSkeleton />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-900 p-4 space-y-3">
            <div className="skeleton h-3 w-24 rounded" />
            <div className="skeleton h-7 w-16 rounded" />
            <div className="skeleton h-2.5 w-20 rounded" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-900 p-5">
          <div className="skeleton h-4 w-40 rounded mb-5" />
          <div className="skeleton h-56 w-full rounded-lg" />
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-900">
          <div className="p-5 pb-0"><div className="skeleton h-4 w-32 rounded" /></div>
          <ListRowsSkeleton rows={4} />
        </div>
      </div>
    </div>
  )
}

// Template detail: heading, tab bar, then field cards.
export function TemplateDetailSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading">
      <HeadingSkeleton />
      <div className="flex items-center gap-6 border-b border-gray-200 dark:border-gray-800 mb-6 pb-3">
        {[64, 52, 80, 70, 58].map((w, i) => (
          <div key={i} className="skeleton h-3.5 rounded" style={{ width: `${w}px` }} />
        ))}
      </div>
      <FormSkeleton sections={2} fields={4} />
    </div>
  )
}
